import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Sparkles, Package, Loader2, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fetchSkillCatalog } from "@/lib/api";
import { toast } from "sonner";

export default function LevelUpDialog({ open, onOpenChange, onConfirm, level }) {
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      return;
    }
    setLoading(true);
    fetchSkillCatalog()
      .then((data) => setAvailable(data.available || []))
      .catch(() => toast.error("Skill-Katalog konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [open]);

  const handleConfirm = async () => {
    if (!selectedId || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(selectedId);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl rounded-none border-2 border-hammerl-ink shadow-brutal-lg bg-hammerl-card p-0 overflow-hidden flex flex-col max-h-[90vh]"
        data-testid="level-up-dialog"
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ scale: 3, rotate: -12, opacity: 0 }}
              animate={{ scale: 1, rotate: -12, opacity: 1 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="absolute top-3 right-4 stamp text-sm pointer-events-none z-10"
            >
              BEFÖRDERT
            </motion.div>
          )}
        </AnimatePresence>

        <DialogHeader className="px-6 pt-6 pb-3 bg-hammerl-highlight border-b-2 border-hammerl-ink shrink-0 text-hammerl-paper">
          <DialogTitle className="font-bold text-2xl tracking-tight flex items-center gap-2">
            <Trophy className="w-6 h-6" /> Stufenaufstieg → Level {level}
          </DialogTitle>
          <DialogDescription className="text-hammerl-ink/80 font-mono text-xs uppercase tracking-widest">
            Wähle eine Fähigkeit · jede gibt ein paar Prozent bessere Wurf-Chance, manche zusätzlich ein Ausrüstungs-Upgrade.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1" data-testid="skill-options-list">
          {loading && (
            <div className="flex items-center gap-2 text-sm font-mono text-hammerl-pencil py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Synergisiere Skill-Vorschläge…
            </div>
          )}

          {!loading && available.map((s) => {
            const isSelected = selectedId === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelectedId(s.id)}
                className={`w-full text-left border-2 p-3 transition-all focus:outline-none ${
                  isSelected
                    ? "border-hammerl-corpblue bg-hammerl-highlight shadow-brutal-sm"
                    : "border-hammerl-ink bg-hammerl-card hover:bg-hammerl-sidebar"
                }`}
                data-testid={`skill-option-${s.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-hammerl-corpblue" />
                    <div className="min-w-0">
                      <div className="font-bold text-base">{s.name}</div>
                      <div className="text-xs text-hammerl-pencil mt-0.5">{s.description}</div>
                    </div>
                  </div>
                  <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 border-2 border-hammerl-ink bg-hammerl-mixed font-mono text-[11px] font-bold text-hammerl-paper">
                    +{s.dice_bonus}% Wurf
                  </span>
                </div>
                {s.item_grant && (
                  <div className="mt-2 ml-6 border-l-2 border-dashed border-hammerl-pencil pl-2 text-[11px] font-mono uppercase tracking-wide text-hammerl-pencil flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    <span>
                      + Item <span className="text-hammerl-ink font-bold">{s.item_grant.name}</span>{" "}
                      <span className="text-hammerl-corpblue">(+{s.item_grant.bonus || 0}% Bonus)</span>
                      {s.replaces && <> · ersetzt &bdquo;{s.replaces}&ldquo;</>}
                    </span>
                  </div>
                )}
              </button>
            );
          })}

          {!loading && available.length === 0 && (
            <div className="text-sm font-mono text-hammerl-pencil text-center py-4">
              Keine Fähigkeiten verfügbar.
            </div>
          )}
        </div>

        <div className="border-t-2 border-hammerl-ink px-6 py-3 flex flex-wrap items-center justify-between gap-2 bg-hammerl-card shrink-0">
          <div className="font-mono text-[11px] uppercase tracking-widest text-hammerl-pencil">
            {selectedId
              ? "Bereit zur Beförderung."
              : "Eine Fähigkeit auswählen, dann bestätigen."}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="rounded-none hover:bg-hammerl-sidebar"
              data-testid="level-up-cancel-btn"
              disabled={submitting}
            >
              Später
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedId || submitting}
              className="rounded-none bg-hammerl-corpblue hover:bg-hammerl-ink text-white font-bold shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
              data-testid="level-up-confirm-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>Beförderung annehmen <ArrowRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
